import { createHand } from '../hand/init';
/** The standard $1/$3 live game the North Star targets. */
export const DEFAULT_STAKES = { sb: 1, bb: 3, currency: 'USD' };
/** The standard $1/$3 full buy-in, so it is where a new table opens. ARBITRARY - capped by nothing. */
export const DEFAULT_BUY_IN = 300;
export const DEFAULT_TABLE_TYPE = 'loosePassive';
/** Float-representation slack for the chip invariant (§5.2). Well below one chip, so real minting
 *  never hides under it; well above IEEE754 noise, so a legal hand never trips it. */
export const CHIP_EPSILON = 1e-6;
/** Open a table. Every seat's sit-down amount is recorded as a `buyin` in the ledger, so
 *  `sum(stacks) === sum(ledger)` holds from hand 1 (§5.2). */
export const newSession = (opts) => {
    const n = opts.roster.length + 1;
    if (n < 2 || n > 9)
        throw new RangeError(`table size must be 2-9, got ${n}`);
    const buyIn = opts.buyIn ?? DEFAULT_BUY_IN;
    if (buyIn <= 0)
        throw new RangeError(`buyIn must be positive, got ${buyIn}`);
    if (opts.startingStacks && opts.startingStacks.length !== n) {
        throw new RangeError(`startingStacks has ${opts.startingStacks.length} entries for a ${n}-handed table`);
    }
    const buttonSeat = opts.buttonSeat ?? 0;
    if (buttonSeat < 0 || buttonSeat >= n) {
        throw new RangeError(`buttonSeat ${buttonSeat} out of range for ${n}-handed table`);
    }
    const handNumber = opts.handNumber ?? 1;
    const tableMode = opts.tableMode ?? 'mid';
    const stackAt = (seat) => opts.startingStacks?.[seat] ?? buyIn;
    const seats = Array.from({ length: n }, (_, seat) => {
        // 'new' is the GH4 cold read - every pod shows '?' and you tag from scratch. 'mid' is the
        // already-diagnosed ring. Hero always knows who he is.
        const read = { revealed: seat === 0 ? true : tableMode === 'mid' };
        if (seat === 0) {
            return { seat, kind: 'hero', displayName: opts.heroName ?? 'You', stack: stackAt(0), read };
        }
        const entry = opts.roster[seat - 1];
        return {
            seat,
            kind: 'villain',
            archetypeId: entry.archetypeId,
            displayName: entry.displayName,
            stack: stackAt(seat),
            read,
        };
    });
    const ledger = seats.map((s) => ({
        type: 'buyin',
        seat: s.seat,
        amount: s.stack,
        handNumber,
    }));
    return {
        sessionId: opts.sessionId,
        stakes: opts.stakes ?? DEFAULT_STAKES,
        tableType: opts.tableType ?? DEFAULT_TABLE_TYPE,
        seats,
        buttonSeat,
        handNumber,
        seedRoot: opts.seedRoot,
        tableMode,
        buyIn,
        ledger,
    };
};
/** The seed for a given hand of this session. Deterministic: seedRoot + handNumber ⇒ the hand. */
export const seedForHand = (session, handNumber = session.handNumber) => `${session.seedRoot}::h${handNumber}`;
/**
 * The roster builds a fresh ScenarioSpec every hand (§4.1).
 *
 * `heroPosition` is DELIBERATELY OMITTED - this is the load-bearing change (§4.2). Pinning it made
 * resolveButtonSeat solve the button BACKWARDS from hero, which is exactly inverted for a rotating
 * table and is why hero was stuck on the button forever. Omitted, the session's buttonSeat drives
 * assignPositions and hero rotates through every position.
 *
 * `tableSize` is `seats.length` - a plain bounded int since TB0 §9 F1. It selects the chart family
 * (normalized `<=6 ? 6 : 9`); the REAL seat count is always `villains.length + 1`.
 */
export const specForHand = (session) => ({
    gameType: 'cash',
    tableSize: session.seats.length,
    stakes: session.stakes,
    heroSeat: 0,
    stackDepthBB: session.buyIn / session.stakes.bb,
    villains: session.seats
        .filter((s) => s.kind === 'villain')
        .map((s) => ({ seat: s.seat, archetypeId: s.archetypeId })),
    tableType: session.tableType,
    seed: seedForHand(session),
});
/** Total chips sitting in front of every seat. */
export const stacksTotal = (session) => session.seats.reduce((sum, s) => sum + s.stack, 0);
/** Total chips ever INJECTED - buy-ins plus reloads. Chips are created nowhere else, so this must
 *  always equal `stacksTotal` between hands (§5.2, the honest ledger identity). */
export const ledgerTotal = (session) => session.ledger.reduce((sum, e) => sum + e.amount, 0);
const playersChipTotal = (state) => state.players.reduce((sum, p) => sum + p.stack, 0);
/**
 * FULL - DEAL a hand, and stop. The first half of the loop (§3 steps 1-2).
 *
 * Split out of `dealNextHand` because a table where **hero decides** (TB0 §3 rule 3) cannot deal and
 * harvest in one call: the user has not acted yet at deal time. The caller deals here, lets the user
 * play the hand for real, and calls `completeHand` with the terminal that produced.
 *
 * Pure: reads the session, mutates nothing.
 */
export const beginHand = (session) => {
    const spec = specForHand(session);
    const startingStacks = session.seats.map((s) => s.stack);
    const handId = `${session.sessionId}:h${session.handNumber}`;
    // Pass buttonSeat EXPLICITLY. Routing through generate()'s picker would ignore the session's
    // button and re-randomize it every hand (§4.2).
    const initial = createHand({ handId, spec, buttonSeat: session.buttonSeat, startingStacks });
    const meta = { handId, spec, buttonSeat: session.buttonSeat, handNumber: session.handNumber, startingStacks };
    return { initial, meta };
};
/**
 * FULL - HARVEST a hand that has been played to completion. The second half of the loop (§3 steps 3-7).
 *
 * Order is fixed and load-bearing: **harvest, then rotate, then reload.** Reloading before harvest
 * would overwrite the result the hand just produced.
 *
 * The terminal is the caller's to produce and this function does not care WHO decided it - that is the
 * whole point of the split. It only insists the hand is finished and that the chips add up.
 *
 * Pure: returns a NEW session, never mutates the one passed in.
 */
export const completeHand = (session, meta, terminal) => {
    const n = session.seats.length;
    const { handId, startingStacks } = meta;
    if (terminal.phase !== 'complete') {
        throw new Error(`playHand returned a '${terminal.phase}' hand; the session can only harvest a complete one`);
    }
    // Invariant (a), §5.2: within a hand the engine neither mints nor destroys chips. This is the real
    // correctness property, with no exceptions - a violation is an engine bug, not bad input.
    //
    // Compared against CHIP_EPSILON rather than exactly: money is held as floats and bet sizings are pot
    // fractions (SIZING_FRACTION carries 1/3 and 2/3, neither exactly representable in binary), so a
    // legitimate hand can land a few ulps off. The gap is not close - real minting shows up at 0.5+
    // chips, six orders of magnitude above this floor - so nothing meaningful hides under it.
    const before = startingStacks.reduce((a, b) => a + b, 0);
    const after = playersChipTotal(terminal);
    if (Math.abs(after - before) > CHIP_EPSILON) {
        throw new Error(`chip conservation violated in ${handId}: ${before} in, ${after} out`);
    }
    // 4. HARVEST - the terminal state is the only source of truth for stacks. advance/showdown already
    //    wrote the ending stacks back; the session copies them, it never recomputes them.
    let seats = session.seats.map((s) => {
        const player = terminal.players.find((p) => p.seat === s.seat);
        if (!player)
            throw new Error(`${handId}: no player at seat ${s.seat} in the terminal state`);
        return { ...s, stack: player.stack };
    });
    // 5/6. ROTATE the button one seat + bump the hand counter.
    const buttonSeat = (session.buttonSeat + 1) % n;
    const handNumber = session.handNumber + 1;
    // 7. RELOAD SWEEP - between hands, NEVER mid-hand (§5.1). A $0 seat cannot be dropped (seats must
    //    stay dense 0..n-1 or every seat behind it renumbers and the fixed roster breaks) and a $0
    //    blind posts all-in for $0, which is a free roll, not poker. So a broke seat reloads - the
    //    seat, the archetype, and your read on it all survive, which is the point.
    const reloads = [];
    seats = seats.map((s) => {
        // `buyIn > stack` guards the pathological buyIn<=bb table: a reload must inject chips, never
        // claw them back, so a seat already at or above the buy-in is left exactly as the hand left it.
        if (s.stack >= session.stakes.bb || session.buyIn <= s.stack)
            return s;
        reloads.push({ type: 'reload', seat: s.seat, amount: session.buyIn - s.stack, handNumber });
        return { ...s, stack: session.buyIn };
    });
    return {
        session: {
            ...session,
            seats,
            buttonSeat,
            handNumber,
            ledger: reloads.length > 0 ? [...session.ledger, ...reloads] : session.ledger,
        },
        hand: { ...meta, terminal },
        reloads,
    };
};
/**
 * Deal, play, harvest, rotate, reload - the whole hand-to-hand loop (§3) in one call.
 *
 * Kept as the one-shot form for callers that CAN produce a terminal at deal time: the engine's own tests,
 * and any headless walk where nobody is waiting to act. A surface where the user decides must use
 * `beginHand` / `completeHand` instead - see the note on `beginHand`.
 *
 * Pure: returns a NEW session, never mutates the one passed in.
 */
export const dealNextHand = (session, deps) => {
    const { initial, meta } = beginHand(session);
    return completeHand(session, meta, deps.playHand(initial, meta));
};
//# sourceMappingURL=session.js.map